import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../components/CustomTableWithPagination/GlobalTable';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import { AddButton } from '../../components/mui/Buttons/Buttons';
import { getMilestoneAchievements } from '../../redux/features/milestoneAchievementSlice';
import { MilestoneTableHeader } from './components/MilestoneTableHeader';
import MilestoneAddEdit from './components/MilestoneAddEdit';
import DeleteMilestone from './components/DeleteMilestone';

/**
 * milestone achievements.
 * one row is one milestone awarded to one employee. the recipient is celebrated with an
 * animated popup exactly once, on their first login on or after the publish date — see
 * MilestoneCelebration, mounted in the app shell.
 * @author Akhil E M
 * @createdAt 27-08-2026
 * @updates 27-08-2026(Akhil EM),
 * @type component
 * @packages react-redux
 */
const MilestoneAchievements = () => {
  const dispatch = useDispatch();
  const { data, meta, loading } = useSelector((state) => state.milestoneAchievement.table);

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 10,
    search: '',
    sort: { field: '', direction: 'asc' },
    frozenColumns: [],
    hiddenColumns: [],
    checkedRowIds: [],
  });

  const [modalState, setModalState] = useState({ actionKey: '', row: null });

  const dispatchParams = useMemo(
    () => ({
      page: tableState.page,
      limit: tableState.pageSize,
      search: tableState.search || '',
    }),
    [tableState.page, tableState.pageSize, tableState.search],
  );

  useEffect(() => {
    const thunk = dispatch(getMilestoneAchievements(dispatchParams));
    //DROPS THE IN FLIGHT REQUEST WHEN THE SEARCH MOVES AGAIN BEFORE IT LANDS
    return () => thunk?.abort();
  }, [dispatch, dispatchParams]);

  const handleRefresh = useCallback(
    () => dispatch(getMilestoneAchievements(dispatchParams)),
    [dispatch, dispatchParams],
  );

  const closeModal = useCallback(
    (shouldRefresh = false) => {
      setModalState({ actionKey: '', row: null });
      if (shouldRefresh) handleRefresh();
    },
    [handleRefresh],
  );

  const handleButtonActionClick = useCallback((actionKey, row) => {
    if (!actionKey || !row) return;
    setModalState({ actionKey, row });
  }, []);

  const modalConfigs = useMemo(
    () => [
      {
        actionKey: 'addMilestone',
        title: 'Add Milestone Achievement',
        render: () => <MilestoneAddEdit onClose={closeModal} />,
      },
      {
        actionKey: 'editMilestone',
        title: 'Edit Milestone Achievement',
        render: ({ row }) => <MilestoneAddEdit row={row} onClose={closeModal} />,
      },
      {
        actionKey: 'deleteMilestone',
        title: 'Delete Milestone Achievement',
        render: ({ row }) => <DeleteMilestone row={row} onClose={closeModal} />,
      },
    ],
    [closeModal],
  );

  const activeConfig = useMemo(
    () => modalConfigs.find((config) => config.actionKey === modalState.actionKey),
    [modalConfigs, modalState.actionKey],
  );

  const headers = useMemo(() => MilestoneTableHeader(), []);

  return (
    <GlobalWrapper title="Milestone Achievements">
      <Box mt={1}>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <AddButton action={() => setModalState({ actionKey: 'addMilestone', row: null })}>
            Add Milestone
          </AddButton>
        </Stack>

        <Paper elevation={3}>
          <Box mt={1}>
            <GlobalTable
              rowUniqueId="milestone_achievement_id"
              headers={headers}
              tableState={tableState}
              setTableState={setTableState}
              loading={loading}
              rows={Array.isArray(data) ? data : []}
              meta={meta}
              showToolTip="milestone_title"
              onButtonActionClick={handleButtonActionClick}
              handleRefresh={handleRefresh}
            />
          </Box>

          <GlobalDialog
            title={activeConfig?.title}
            open={Boolean(modalState?.actionKey)}
            setOpen={(val) => {
              if (!val) setModalState({ actionKey: '', row: null });
            }}
            closeButton
            content={activeConfig?.render ? activeConfig.render({ row: modalState.row }) : null}
            maxWidth="sm"
            fullWidth
          />
        </Paper>
      </Box>
    </GlobalWrapper>
  );
};

export default MilestoneAchievements;
